/**
 * Fetch-based HTTP client для замены axios
 * Поддерживает AbortController для отмены запросов
 */

export interface FetchClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface RequestConfig {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeout?: number;
}

export interface FetchResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

export class FetchClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;

  constructor(config: FetchClientConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.timeout = config.timeout || 30000;
    this.defaultHeaders = config.headers || {};
  }

  /**
   * GET запрос
   */
  async get<T = any>(url: string, config: RequestConfig = {}): Promise<FetchResponse<T>> {
    const queryString = this.buildQueryString(config.params);
    const fullUrl = this.buildUrl(url) + (queryString ? `?${queryString}` : '');
    
    return this.request<T>('GET', fullUrl, undefined, config);
  }

  /**
   * POST запрос
   */
  async post<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<FetchResponse<T>> {
    return this.request<T>('POST', this.buildUrl(url), data, config);
  }

  /**
   * PUT запрос
   */
  async put<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<FetchResponse<T>> {
    return this.request<T>('PUT', this.buildUrl(url), data, config);
  }

  /**
   * DELETE запрос
   */
  async delete<T = any>(url: string, config: RequestConfig = {}): Promise<FetchResponse<T>> {
    return this.request<T>('DELETE', this.buildUrl(url), undefined, config);
  }

  /**
   * Основной метод для выполнения запросов
   */
  private async request<T = any>(
    method: string,
    url: string,
    data?: any,
    config: RequestConfig = {}
  ): Promise<FetchResponse<T>> {
    const controller = new AbortController();
    const timeoutMs = config.timeout || this.timeout;
    
    // Устанавливаем таймаут
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    // Используем переданный signal или создаем новый
    const signal = config.signal || controller.signal;

    try {
      const headers = {
        ...this.defaultHeaders,
        ...config.headers,
      };

      // Добавляем Content-Type для POST/PUT с данными
      if (data && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal,
      });

      clearTimeout(timeoutId);

      // Парсим ответ
      let responseData: T;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text() as any;
      }

      // Проверяем статус
      if (!response.ok) {
        throw this.createError(
          `Request failed with status ${response.status}`,
          response,
          responseData
        );
      }

      return {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw this.createError('Request timeout', undefined, undefined, 'ETIMEDOUT');
      }

      throw error;
    }
  }

  /**
   * Построить полный URL
   */
  private buildUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    const base = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    const path = url.startsWith('/') ? url : `/${url}`;
    
    return base + path;
  }

  /**
   * Построить query string из параметров
   */
  private buildQueryString(params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Создать объект ошибки в формате похожем на axios
   */
  private createError(
    message: string,
    response?: Response,
    data?: any,
    code?: string
  ): any {
    const error: any = new Error(message);
    error.code = code;
    
    if (response) {
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data,
        headers: response.headers,
      };
    }

    return error;
  }
}

/**
 * Создать экземпляр FetchClient
 */
export function createFetchClient(config: FetchClientConfig = {}): FetchClient {
  return new FetchClient(config);
}
